/**
 * Cookie Utilities
 * Single source of truth for cookie operations
 * Used by both client and server
 */

import { logger } from "./logger";

// Cookie configuration
const COOKIE_CONFIG = {
  ACCESS_TOKEN: 'access_token',
  EMAIL_VERIFIED: 'email_verified',
  PHONE_VERIFIED: 'phone_verified',
  KYC_VERIFIED: 'kyc_verified',
  REGISTRATION_EMAIL_PENDING: 'registration_email_pending',
  REGISTRATION_PHONE_PENDING: 'registration_phone_pending',
} as const;

export const COOKIES = COOKIE_CONFIG;

// Cookie options
export interface CookieOptions {
  maxAge?: number; // in seconds
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Determine if we're in a local development environment
 */
export function isLocalDevelopment(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.16.') ||
    hostname.startsWith('172.17.') ||
    hostname.startsWith('172.18.') ||
    hostname.startsWith('172.19.') ||
    hostname.startsWith('172.20.') ||
    hostname.startsWith('172.21.') ||
    hostname.startsWith('172.22.') ||
    hostname.startsWith('172.23.') ||
    hostname.startsWith('172.24.') ||
    hostname.startsWith('172.25.') ||
    hostname.startsWith('172.26.') ||
    hostname.startsWith('172.27.') ||
    hostname.startsWith('172.28.') ||
    hostname.startsWith('172.29.') ||
    hostname.startsWith('172.30.') ||
    hostname.startsWith('172.31.')
  );
}

/**
 * Get default cookie options based on environment
 */
export function getDefaultCookieOptions(): CookieOptions {
  if (typeof window === 'undefined') {
    // Server-side: use NODE_ENV
    const isProduction = process.env.NODE_ENV === 'production';
    return {
      path: '/',
      secure: isProduction,
      sameSite: 'strict',
      maxAge: 60 * 60 * 24 * 7, // 7 days
    };
  }

  // Client-side: check hostname
  const isLocal = isLocalDevelopment(window.location.hostname);
  return {
    path: '/',
    secure: !isLocal,
    sameSite: 'strict',
    maxAge: 60 * 60 * 24 * 7, // 7 days
  };
}

/**
 * CLIENT-SIDE: Set a cookie
 */
export function setCookie(
  name: string,
  value: string,
  options?: Partial<CookieOptions>
): void {
  if (typeof window === 'undefined') {
    logger.warn('setCookie called on server-side, use server action instead');
    return;
  }

  const defaultOptions = getDefaultCookieOptions();
  const finalOptions = { ...defaultOptions, ...options };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (finalOptions.maxAge) {
    cookieString += `; Max-Age=${finalOptions.maxAge}`;
  }

  if (finalOptions.expires) {
    cookieString += `; Expires=${finalOptions.expires.toUTCString()}`;
  }

  if (finalOptions.path) {
    cookieString += `; Path=${finalOptions.path}`;
  }

  if (finalOptions.domain) {
    cookieString += `; Domain=${finalOptions.domain}`;
  }

  if (finalOptions.secure) {
    cookieString += '; Secure';
  }

  if (finalOptions.httpOnly) {
    logger.warn('HttpOnly cookies can only be set server-side');
  }

  if (finalOptions.sameSite) {
    cookieString += `; SameSite=${finalOptions.sameSite.charAt(0).toUpperCase() + finalOptions.sameSite.slice(1)}`;
  }

  document.cookie = cookieString;
  logger.log(`🍪 [Client] Cookie set: ${name}=${value}`);
}

/**
 * CLIENT-SIDE: Get a cookie value
 */
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${encodeURIComponent(name)}=`);

  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }

  return null;
}

/**
 * CLIENT-SIDE: Remove a cookie
 */
export function removeCookie(name: string): void {
  if (typeof window === 'undefined') {
    logger.warn('removeCookie called on server-side, use server action instead');
    return;
  }

  document.cookie = `${encodeURIComponent(name)}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  logger.log(`🗑️ [Client] Cookie removed: ${name}`);
}

/**
 * CLIENT-SIDE: Remove all auth-related cookies
 */
export function removeAllAuthCookies(): void {
  if (typeof window === 'undefined') return;

  removeCookie(COOKIES.ACCESS_TOKEN);
  removeCookie(COOKIES.EMAIL_VERIFIED);
  removeCookie(COOKIES.PHONE_VERIFIED);
  removeCookie(COOKIES.KYC_VERIFIED);
  removeCookie(COOKIES.REGISTRATION_EMAIL_PENDING);
  removeCookie(COOKIES.REGISTRATION_PHONE_PENDING);

  logger.log('🗑️ [Client] All auth cookies removed');
}
